import React, { useEffect, useRef, useState } from "react";
import { axiosInstance } from "../utils/axiosConfig";
import toast from "react-hot-toast";

interface Turn {
  role: "user" | "assistant";
  content: string;
}

interface AIAssistantPanelProps {
  isOpen: boolean;
  onClose: () => void;
}

const STARTER_PROMPTS = [
  "What should I do next?",
  "Which applications need a follow-up?",
  "Help me prep for my next interview",
  "Why am I not getting interviews?",
];

const AIAssistantPanel: React.FC<AIAssistantPanelProps> = ({ isOpen, onClose }) => {
  const [history, setHistory] = useState<Turn[]>([]);
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const scrollRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (isOpen && history.length === 0) {
      setHistory([
        {
          role: "assistant",
          content:
            "Hi — I'm your Sumbitrbuddy assistant. I can see your applications, interviews, and resume status, so ask me anything about your search. Try one of the prompts below to get started.",
        },
      ]);
    }
  }, [isOpen, history.length]);

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [history, sending]);

  const send = async (text: string) => {
    const trimmed = text.trim();
    if (!trimmed || sending) return;
    const next: Turn[] = [...history, { role: "user", content: trimmed }];
    setHistory(next);
    setInput("");
    setSending(true);
    try {
      const resp = await axiosInstance.post("/auth/ai/assistant", {
        message: trimmed,
        history: next.slice(0, -1).slice(-8),
      });
      setHistory((h) => [...h, { role: "assistant", content: resp.data.reply || "..." }]);
    } catch (err: any) {
      // The assistant is available on ALL plans (2026-07-14). The only
      // expected 403 here is the AI-review consent gate.
      if (err?.response?.status === 403 && err?.response?.data?.consentRequired) {
        toast("Enable AI review consent to chat with the assistant.", { icon: "🔐" });
        setHistory((h) => [
          ...h,
          {
            role: "assistant",
            content:
              "Before I can help, I need your permission to read your profile and resume. Go to Settings → Privacy & Permissions and enable \"AI Resume Review\", then come back and ask me anything.",
          },
        ]);
      } else {
        toast.error("Assistant is unavailable right now");
        setHistory((h) => [
          ...h,
          { role: "assistant", content: "I couldn't reach the AI just now. Please try again in a moment." },
        ]);
      }
    } finally {
      setSending(false);
    }
  };

  if (!isOpen) return null;

  return (
    <>
      <div
        onClick={onClose}
        style={{
          position: "fixed",
          inset: 0,
          background: "rgba(0,0,0,0.25)",
          zIndex: 998,
        }}
      />
      <aside
        style={{
          position: "fixed",
          top: 0,
          right: 0,
          height: "100vh",
          width: "min(420px, 100vw)",
          background: "#ffffff",
          borderLeft: "1px solid #e5e7eb",
          boxShadow: "-4px 0 20px rgba(0,0,0,0.08)",
          zIndex: 999,
          display: "flex",
          flexDirection: "column",
          color: "#1f2937",
        }}
        role="dialog"
        aria-label="AI Assistant"
      >
        <header
          style={{
            padding: "16px 20px",
            borderBottom: "1px solid #e5e7eb",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <div>
            <div style={{ fontWeight: 600, fontSize: 16 }}>🤖 AI Assistant</div>
            <div style={{ fontSize: 12, color: "#6b7280", marginTop: 2 }}>
              Grounded in your live applications
            </div>
          </div>
          <button
            onClick={onClose}
            aria-label="Close assistant"
            style={{
              background: "transparent",
              border: "none",
              fontSize: 22,
              color: "#6b7280",
              cursor: "pointer",
              padding: 4,
            }}
          >
            ×
          </button>
        </header>

        <div
          ref={scrollRef}
          style={{
            flex: 1,
            overflowY: "auto",
            padding: 16,
            display: "flex",
            flexDirection: "column",
            gap: 10,
          }}
        >
          {history.map((t, i) => (
            <div
              key={i}
              style={{
                alignSelf: t.role === "user" ? "flex-end" : "flex-start",
                maxWidth: "85%",
                background: t.role === "user" ? "#2563eb" : "#f3f4f6",
                color: t.role === "user" ? "#ffffff" : "#1f2937",
                padding: "10px 14px",
                borderRadius: 12,
                fontSize: 14,
                lineHeight: 1.5,
                whiteSpace: "pre-wrap",
                wordBreak: "break-word",
              }}
            >
              {t.content}
            </div>
          ))}
          {sending && (
            <div
              style={{
                alignSelf: "flex-start",
                background: "#f3f4f6",
                color: "#6b7280",
                padding: "10px 14px",
                borderRadius: 12,
                fontSize: 14,
              }}
            >
              <span>thinking…</span>
            </div>
          )}
        </div>

        {history.length <= 1 && (
          <div style={{ padding: "0 16px 8px" }}>
            <div style={{ fontSize: 11, color: "#6b7280", marginBottom: 6, textTransform: "uppercase", letterSpacing: 0.5 }}>
              Try
            </div>
            <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
              {STARTER_PROMPTS.map((p) => (
                <button
                  key={p}
                  onClick={() => send(p)}
                  disabled={sending}
                  style={{
                    background: "#ffffff",
                    border: "1px solid #e5e7eb",
                    color: "#1f2937",
                    padding: "6px 10px",
                    borderRadius: 16,
                    fontSize: 12,
                    cursor: "pointer",
                  }}
                >
                  {p}
                </button>
              ))}
            </div>
          </div>
        )}

        <form
          onSubmit={(e) => {
            e.preventDefault();
            send(input);
          }}
          style={{
            padding: 12,
            borderTop: "1px solid #e5e7eb",
            display: "flex",
            gap: 8,
            background: "#ffffff",
          }}
        >
          <input
            value={input}
            onChange={(e) => setInput(e.target.value)}
            placeholder="Ask about your job search…"
            disabled={sending}
            style={{
              flex: 1,
              padding: "10px 12px",
              border: "1px solid #e5e7eb",
              borderRadius: 8,
              fontSize: 14,
              color: "#1f2937",
              background: "#ffffff",
              outline: "none",
            }}
          />
          <button
            type="submit"
            disabled={sending || !input.trim()}
            style={{
              background: "#2563eb",
              color: "#ffffff",
              border: "none",
              borderRadius: 8,
              padding: "0 16px",
              fontSize: 14,
              fontWeight: 500,
              cursor: sending || !input.trim() ? "not-allowed" : "pointer",
              opacity: sending || !input.trim() ? 0.5 : 1,
            }}
          >
            Send
          </button>
        </form>
      </aside>
    </>
  );
};

export default AIAssistantPanel;
